import { makeAutoObservable } from "mobx";
import { DateTime } from "luxon";
import RootStore from "stores/root-store";
import { getEnv, getRoot } from "mobx-easy";
import i18n from "../../../i18n";
import { RootEnv } from "stores/setup/create-store";
import { ProductAnalyticsEvents } from "enum/ProductAnalyticsEvents";
import { StoreState } from "enum/StoreState";
import { IColorMap, ILayerInfo, IMapTileInfo } from "types/IForecastMap";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";

export class ForecastMapStore {
  state: string = StoreState.DONE;
  layersData: ILayerInfo | null = null;
  timelineData: {
    allFrames: IMapTileInfo[];
    totalFrames: number;
  } | null = null;
  currentIndex: number = 0;
  isPlaying: boolean = false;
  intervalId: NodeJS.Timeout | null = null;
  colormap: IColorMap | null = null;
  show: boolean = false;
  isShowClicked: boolean = false;
  environmentalVariables = [
    EnvironmentalVariables.WIND_VEL,
    EnvironmentalVariables.WIND_VEL_80M,
    EnvironmentalVariables.WIND_VEL_100M,
    EnvironmentalVariables.GUST,
    EnvironmentalVariables.GUST_80M,
    EnvironmentalVariables.GUST_100M,
    EnvironmentalVariables.VISIBILITY,
    EnvironmentalVariables.PRECIP,
    EnvironmentalVariables.TEMP_AIR,
    EnvironmentalVariables.SLP,
    EnvironmentalVariables.DEW_POINT,
    EnvironmentalVariables.TOTAL_CLOUD_COVER,
  ];
  selectedEnvironmentalVariable: EnvironmentalVariables | null = null;
  drawerLayerOpen: boolean = false;
  currentTimestemp: number | null = null;
  maxZoomMap: number | undefined = undefined;
  isInitialLayer: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  setState(value: StoreState) {
    this.state = value;
  }

  reset() {
    this.setState(StoreState.DONE);
    this.layersData = null;
    this.timelineData = null;
    this.currentIndex = 0;
    this.stop();
    this.isPlaying = false;
    this.intervalId = null;
    this.show = false;
    this.isShowClicked = false;
    this.selectedEnvironmentalVariable = null;
    this.drawerLayerOpen = false;
    this.currentTimestemp = null;
    this.maxZoomMap = undefined;
    this.isInitialLayer = true;
  }

  setLayersData(value: ILayerInfo | null) {
    this.layersData = value;
  }

  setTimelineData(
    value: {
      allFrames: IMapTileInfo[];
      totalFrames: number;
    } | null
  ) {
    this.timelineData = value;
  }

  setIsPlaying(value: boolean) {
    this.isPlaying = value;
  }

  setIntervalId(value: NodeJS.Timeout | null) {
    this.intervalId = value;
  }

  setSelectedEnvironmentalVariable(value: EnvironmentalVariables | null) {
    this.selectedEnvironmentalVariable = value;
  }

  setShow(value: boolean) {
    localStorage.setItem("firstViewForecast", "true");
    this.setIsShowClicked(true);
    this.show = value;
    if (value && this.selectedEnvironmentalVariable && !this.isInitialLayer) {
      this.addProductAnalyticsEvent(
        ProductAnalyticsEvents.ForecastLayerMapView,
        { environmental_variable: this.selectedEnvironmentalVariable }
      );
    } else {
      this.setIsInitialLayer(false);
    }
  }

  setIsShowClicked(value: boolean) {
    this.isShowClicked = value;
  }

  setDrawerLayerOpen(value: boolean) {
    this.drawerLayerOpen = value;
  }

  setCurrentTimestemp(value: number | null) {
    this.currentTimestemp = value;
  }

  setMaxZoomMap(value: number | undefined) {
    this.maxZoomMap = value;
  }

  setIsInitialLayer(value: boolean) {
    this.isInitialLayer = value;
  }

  incrementIndex() {
    this.currentIndex++;
  }

  decrementIndex() {
    this.currentIndex--;
  }

  resetIndex() {
    this.currentIndex = 0;
  }

  async fetchMapLayers() {
    const { forecastMapService } = getEnv<RootEnv>();
    this.setState(StoreState.PEDDING);
    this.setLayersData(null);

    if (!this.selectedEnvironmentalVariable) return;

    try {
      const response = await forecastMapService.getForecastMapInfo(
        this.selectedEnvironmentalVariable
      );

      if (response.tiles?.length > 0 && this.layersData === null) {
        if (this.currentTimestemp) {
          this.currentIndex = this.findClosestIndexToNow(
            response.tiles,
            this.currentTimestemp
          );
        } else {
          this.currentIndex = this.findClosestIndexToNow(response.tiles);
        }

        if (this.selectedEnvironmentalVariable.includes("wave")) {
          this.setMaxZoomMap(12);
        } else {
          this.setMaxZoomMap(10);
        }

        this.setLayersData(response);
        this.setTimelineData({
          allFrames: response.tiles,
          totalFrames: response.tiles.length,
        });
        this.setState(StoreState.DONE);
        return { success: true };
      }

      if (!response.tiles?.length) {
        this.setLayersData(null);
        this.setTimelineData(null);
        this.setShow(false);
        return { success: false };
      }
    } catch (error) {
      this.setLayersData(null);
      this.setTimelineData(null);
      console.error("Error fetching radar data: ", error);
    }
  }

  play(intervalSeconds = 1) {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.intervalId = setInterval(() => {
      if (!this.layersData) return;

      if (this.currentIndex < this.totalFrames - 1) {
        this.incrementIndex();
      } else {
        this.restart();
      }
    }, intervalSeconds * 1000);
  }

  stop() {
    this.setIsPlaying(false);
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.setIntervalId(null);
    }
  }

  restart() {
    this.resetIndex();
    this.play();
  }

  setCurrentIndex(index: number) {
    this.currentIndex = index;
    this.stop();
  }

  get isLastIndex() {
    if (!this.layersData) return false;
    return this.currentIndex === this.totalFrames - 1;
  }

  get currentMapUrl() {
    if (!this.layersData) return null;
    const host = this.layersData.host;
    const tiles = this.layersData.tiles;
    const currentPaths = { ...tiles[this.currentIndex]?.path };
    for (const path in currentPaths) {
      currentPaths[path] = this.buildUrl(host, currentPaths[path]);
    }
    const bounds = this.layersData.bounds;
    return { path: currentPaths, bounds };
  }

  get prevMapUrl() {
    if (!this.layersData) return null;

    if (this.currentIndex === 0) {
      return this.currentMapUrl;
    }

    const host = this.layersData.host;
    const tiles = this.layersData.tiles;
    const prevPaths = { ...tiles[this.currentIndex - 1]?.path };
    for (const path in prevPaths) {
      prevPaths[path] = this.buildUrl(host, prevPaths[path]);
    }
    return prevPaths;
  }

  get currentTimestamp() {
    if (!this.layersData) return "";
    const tiles = this.layersData.tiles;
    return tiles[this.currentIndex]?.time;
  }

  buildUrl(host: string, path: string) {
    const format = "webp";
    return `${host}${path}/{z}/{x}/{y}.${format}`;
  }

  get elapsedTime() {
    const {
      dataStores: { userConfigStore },
    } = getRoot<RootStore>();

    if (!this.currentTimestamp) {
      return "";
    }

    const currentTime = DateTime.fromSeconds(this.currentTimestamp, {
      zone: userConfigStore.timezone,
    });

    const timeString = currentTime
      .setLocale(i18n.language)
      .toFormat("ccc dd - HH:mm");

    return `${timeString}`;
  }

  async addProductAnalyticsEvent(
    event: ProductAnalyticsEvents,
    eventProperty: Record<string, any>
  ) {
    const { logServices } = getEnv<RootEnv>();
    try {
      await logServices.addProductAnalyticsEvent(event, eventProperty);
    } catch (error) {
      console.error(`Error while call addProductAnalytics event`);
    }
  }

  findSmallestContainingBound(
    lat: number,
    lng: number,
    bounds: Record<string, [[number, number], [number, number]]>
  ): string | null {
    let smallestBoundKey: string | null = null;
    let smallestArea = Infinity;

    for (const [key, bound] of Object.entries(bounds)) {
      const [[south, west], [north, east]] = bound;

      if (lat >= south && lat <= north && lng >= west && lng <= east) {
        const area = (north - south) * (east - west);

        if (smallestBoundKey === null || area < smallestArea) {
          smallestBoundKey = key;
          smallestArea = area;
        }
      }
    }

    return smallestBoundKey;
  }

  private findClosestIndexToNow(
    items: IMapTileInfo[],
    timestampInSeconds?: number
  ): number {
    const referenceTime = timestampInSeconds ?? Math.floor(Date.now() / 1000);
    let closestIndex = 0;
    let smallestDiff = Math.abs(items[0].time - referenceTime);

    for (let i = 1; i < items.length; i++) {
      const diff = Math.abs(items[i].time - referenceTime);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  get totalFrames() {
    const totalFrames = this.layersData?.tiles.length;
    if (!totalFrames) return 0;
    return totalFrames;
  }
}
